import type { AttributionOrder } from "./channel-config"

export type AttributionModelKey = "last_click" | "first_click" | "last_non_direct_click"

export interface AttributionModelOption {
  key: AttributionModelKey
  label: string
  description: string
  default?: boolean
}

export const ATTRIBUTION_MODEL_OPTIONS: AttributionModelOption[] = [
  {
    key: "first_click",
    label: "First Click",
    description: "Credits the first visit in the order's journey. If it was direct, the last visit gets the credit.",
  },
  {
    key: "last_click",
    label: "Last Click",
    description: "Credits the last visit in the order's journey. If it was direct, the first visit gets the credit.",
    default: true,
  },
  {
    key: "last_non_direct_click",
    label: "Last Non-Direct Click",
    description:
      "Credits the last visit in the order's journey that wasn't direct: an ad, search, social, email or referral link.",
  },
]

export const DEFAULT_ATTRIBUTION_MODEL: AttributionModelKey = "last_click"

export function parseAttributionModel(input: string | null | undefined): AttributionModelKey {
  if (!input) {
    return DEFAULT_ATTRIBUTION_MODEL
  }

  const normalized = input.trim().toLowerCase().replace(/[-\s]+/g, "_") as AttributionModelKey
  const isSupported = ATTRIBUTION_MODEL_OPTIONS.some((option) => option.key === normalized)

  return isSupported ? normalized : DEFAULT_ATTRIBUTION_MODEL
}

const JOURNEY_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "referral_code",
  "referrer_url",
  "landing_page",
  "shopify_source",
  "shopify_source_type",
] as const

export type JourneyField = (typeof JOURNEY_FIELDS)[number]
export type JourneySide = "first" | "last"
type JourneyColumn = `${JourneySide}_${JourneyField}`

/** One session of an order's journey, read from the `first_*` or the `last_*` columns. */
export type JourneyVisit = Record<JourneyField, string | null>

/** A session of the stored timeline (`journey_moments`, ascending `occurred_at`). */
export type StoredJourneyMoment = JourneyVisit & { occurred_at: string }

/** Fields that make a visit a source on their own; a referrer counts only from outside the store (`hasSignal`). */
const SIGNAL_FIELDS: JourneyField[] = ["utm_source", "utm_medium", "utm_campaign", "referral_code"]

/** Shopify `source` values that mean "no source": direct visits and the literal phrase it uses for unknown ones. */
const SOURCELESS_SHOPIFY_SOURCES = new Set(["direct", "unknown", "an unknown source"])

const columnKey = (side: JourneySide, field: JourneyField): JourneyColumn => `${side}_${field}`

const isMeaningfulValue = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0

/**
 * A model credits one whole visit: the preferred side when it has a signal, else the other side
 * when it has one, else the preferred (direct) side. Fields are never mixed across sessions.
 * Last Non-Direct Click reads the stored timeline instead and falls back to the last visit when
 * every stored session is direct or the timeline has not been backfilled yet.
 */
export function selectVisit(order: AttributionOrder, model: AttributionModelKey): JourneyVisit {
  if (model === "last_non_direct_click") {
    const timeline = order.journey_moments?.map(readMoment) ?? []
    if (timeline.length === 0) {
      return selectVisit(order, "last_click")
    }
    return timeline.findLast(hasSignal) ?? readVisit(order, "last")
  }

  const preferredSide: JourneySide = model === "first_click" ? "first" : "last"
  const otherSide: JourneySide = preferredSide === "first" ? "last" : "first"

  const preferred = readVisit(order, preferredSide)
  if (hasSignal(preferred)) {
    return preferred
  }

  const other = readVisit(order, otherSide)
  return hasSignal(other) ? other : preferred
}

export function readVisit(order: AttributionOrder, side: JourneySide): JourneyVisit {
  return normalizedVisit((field) => order[columnKey(side, field)])
}

/** A stored timeline element read like a column visit: trimmed, blanks and missing fields → null. */
export function readMoment(moment: StoredJourneyMoment): StoredJourneyMoment {
  return { occurred_at: moment.occurred_at, ...normalizedVisit((field) => moment[field]) }
}

function normalizedVisit(read: (field: JourneyField) => unknown): JourneyVisit {
  const visit = {} as JourneyVisit
  for (const field of JOURNEY_FIELDS) {
    const value = read(field)
    visit[field] = isMeaningfulValue(value) ? value.trim() : null
  }
  return visit
}

export function hasSignal(visit: JourneyVisit): boolean {
  if (SIGNAL_FIELDS.some((field) => visit[field] !== null) || visit.shopify_source_type !== null) {
    return true
  }
  // Shopify knows the store's domains, so its classification decides whether a referrer is a source;
  // visits read before it was stored compare the referrer with the visit's own site instead.
  if (visit.shopify_source !== null) {
    return !SOURCELESS_SHOPIFY_SOURCES.has(visit.shopify_source.toLowerCase())
  }
  return visit.referrer_url !== null && !isSameSite(visit.referrer_url, visit.landing_page)
}

function isSameSite(referrerUrl: string, landingPage: string | null): boolean {
  const referrer = siteHost(referrerUrl)
  const landing = landingPage ? siteHost(landingPage) : null
  if (!referrer || !landing) {
    return false
  }
  return referrer === landing || referrer.endsWith(`.${landing}`) || landing.endsWith(`.${referrer}`)
}

function siteHost(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "") || null
  } catch {
    return null
  }
}

/** Writes the selected visit into both column sides, so downstream `first || last` reads cannot re-mix sessions. */
export function reshapeOrderForModel(order: AttributionOrder, model: AttributionModelKey): AttributionOrder {
  const visit = selectVisit(order, model)
  const columns: Partial<Record<JourneyColumn, string | null>> = {}

  for (const field of JOURNEY_FIELDS) {
    columns[columnKey("first", field)] = visit[field]
    columns[columnKey("last", field)] = visit[field]
  }

  return { ...order, ...columns }
}

export function selectJourneyValue(
  order: AttributionOrder,
  field: JourneyField,
  model: AttributionModelKey,
): string | null {
  return selectVisit(order, model)[field]
}
